if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        FoxTheme.Motion.inView(this.closest('.countdown__wrapper'), this.init.bind(this), {
          margin: '200px 0px 0px 0px',
        });
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
        };
        this.units = ['day', 'hour', 'minute'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.timezone = this.dataset.timezone;
        this.weekday = this.dataset.weekday === undefined ? null : Number(this.dataset.weekday);
        this.timeOfDay = this.parseTimeOfDay(this.dataset.timeOfDay);
        this.loop = false;
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time':
            const countdownData = this.dataset.dueDate;
            if (countdownData) {
              this.targetTime = this.timezone
                ? this.parseFixedTimeInTimezone(countdownData)
                : new Date(countdownData).getTime();
            }
            break;
          default: // Evergreen.
            this.targetTime = this.getEvergreenDueDate().getTime();
            break;
        }

        if (!this.targetTime) {
          return;
        }

        this.startTime = Date.now();
        if (this.targetTime > this.startTime) {
          this.fetchDomElements();
          this.beginCountdown();
        }
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();
          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);
        this.classList.remove('hidden');
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      parseFixedTimeInTimezone(value) {
        const match = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(value.trim());
        if (!match) {
          return new Date(value).getTime();
        }

        let hour = Number(match[4]);
        const meridiem = match[6] && match[6].toUpperCase();
        if (meridiem === 'PM' && hour < 12) hour += 12;
        if (meridiem === 'AM' && hour === 12) hour = 0;

        return this.zonedWallTimeToTimestamp(
          Number(match[1]),
          Number(match[2]),
          Number(match[3]),
          hour,
          Number(match[5]),
          0
        );
      }

      parseTimeOfDay(value) {
        const match = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
        if (!match) {
          return { hour: 0, minute: 0 };
        }
        return {
          hour: Math.min(23, Math.max(0, Number(match[1]))),
          minute: Math.min(59, Math.max(0, Number(match[2]))),
        };
      }

      terminateCountdown() {
        clearInterval(this.intervalId);
        if (this.type !== 'fixed_time') {
          this.targetTime = this.getEvergreenDueDate().getTime();
          this.beginCountdown();
        } else if (this.loop) {
          this.beginCountdown();
        } else {
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = '00';
          }
        });
      }

      getEvergreenDueDate() {
        if (this.timezone) {
          return this.getZonedEvergreenDueDate();
        }

        let intervalHrs;
        const now = new Date();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
            dueDate.setDate(1); // Start from the 1st day of the next month.
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week':
            const dayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - dayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return dueDate;
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }

      getZonedNow() {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: this.timezone,
          hourCycle: 'h23',
          weekday: 'short',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
          .formatToParts(new Date())
          .reduce((acc, part) => {
            if (part.type !== 'literal') acc[part.type] = part.value;
            return acc;
          }, {});

        const weekdayIndex = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

        return {
          year: Number(parts.year),
          month: Number(parts.month),
          day: Number(parts.day),
          hour: Number(parts.hour),
          minute: Number(parts.minute),
          second: Number(parts.second),
          weekday: weekdayIndex[parts.weekday],
        };
      }

      getTimezoneOffsetMs(date) {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: this.timezone,
          hourCycle: 'h23',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
          .formatToParts(date)
          .reduce((acc, part) => {
            if (part.type !== 'literal') acc[part.type] = part.value;
            return acc;
          }, {});

        const asUTC = Date.UTC(
          Number(parts.year),
          Number(parts.month) - 1,
          Number(parts.day),
          Number(parts.hour),
          Number(parts.minute),
          Number(parts.second)
        );

        // Strip sub-second ms — formatToParts rounds to whole seconds
        return asUTC - Math.floor(date.getTime() / 1000) * 1000;
      }

      zonedWallTimeToTimestamp(year, month, day, hour, minute, second) {
        const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
        const offset = this.getTimezoneOffsetMs(new Date(utcGuess));
        return utcGuess - offset;
      }

      getNextZonedWeekday(now, targetWeekday, hour, minute) {
        const daysUntil = (targetWeekday - now.weekday + 7) % 7;
        const target = new Date(Date.UTC(now.year, now.month - 1, now.day));
        target.setUTCDate(target.getUTCDate() + daysUntil);

        const computeDue = () =>
          this.zonedWallTimeToTimestamp(
            target.getUTCFullYear(),
            target.getUTCMonth() + 1,
            target.getUTCDate(),
            hour,
            minute,
            0
          );

        let dueTimestamp = computeDue();
        if (dueTimestamp <= Date.now()) {
          target.setUTCDate(target.getUTCDate() + 7);
          dueTimestamp = computeDue();
        }

        return new Date(dueTimestamp);
      }

      getZonedEvergreenDueDate() {
        const now = this.getZonedNow();

        if (this.duration === 'every_week') {
          const targetWeekday = Number.isInteger(this.weekday) ? this.weekday : 0;
          return this.getNextZonedWeekday(now, targetWeekday, this.timeOfDay.hour, this.timeOfDay.minute);
        }

        if (this.duration === 'every_month') {
          const nextMonth = now.month === 12 ? 1 : now.month + 1;
          const nextMonthYear = now.month === 12 ? now.year + 1 : now.year;
          return new Date(this.zonedWallTimeToTimestamp(nextMonthYear, nextMonth, 1, 0, 0, 0));
        }

        const intervalHrsMap = {
          every_2_hrs: 2,
          every_6_hrs: 6,
          every_12_hrs: 12,
          every_24_hrs: 24,
        };
        const intervalHrs = intervalHrsMap[this.duration] || 24;
        const nextBoundaryHour = (Math.floor(now.hour / intervalHrs) + 1) * intervalHrs;

        const target = new Date(Date.UTC(now.year, now.month - 1, now.day));
        let boundaryHour = nextBoundaryHour;
        if (nextBoundaryHour >= 24) {
          target.setUTCDate(target.getUTCDate() + 1);
          boundaryHour = 0;
        }

        return new Date(
          this.zonedWallTimeToTimestamp(
            target.getUTCFullYear(),
            target.getUTCMonth() + 1,
            target.getUTCDate(),
            boundaryHour,
            0,
            0
          )
        );
      }
    }
  );
}
